import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view'
import { Box, Typography, Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { formatDate } from '../../../../utils/formatDate'

const PageList = ({ sitemap, prevSitemap, checkedPages, setCheckedPages, handleOpenDeleteDialog }) => {
  const [newPages, setNewPages] = useState([])
  const [expandedItems, setExpandedItems] = useState([])
  const [tooltipOpen, setTooltipOpen] = useState('')

  const navigate = useNavigate()

  const getPageIds = (pages) => {
    return pages.flatMap(page => [
      page.id,
      ...(page.children ? getPageIds(page.children) : [])
    ])
  }

  useEffect(() => {
    setExpandedItems(
      sitemap.flatMap(page => [
        page.id,
        ...(page.children ? getPageIds(page.children) : [])
      ])
    )

    if (sitemap.length && prevSitemap.length && sitemap[0].project_id === prevSitemap[0].project_id) {
      const prevPageIds = getPageIds(prevSitemap)
      const currentPageIds = getPageIds(sitemap)

      const newlyAddedPages = currentPageIds.filter(id => !prevPageIds.includes(id))
      setNewPages(newlyAddedPages)
    }
  // eslint-disable-next-line
  }, [sitemap, prevSitemap])

  const handleToggle = (event, pageId) => {
    const isChecked = event.target.checked

    setCheckedPages((prev) =>
      isChecked
        ? [...prev, pageId]
        : prev.filter((id) => id !== pageId)
    )
  }

  const handleToggleExpansion = (event, nodeIds) => {
    setExpandedItems(nodeIds)
  }

  const handleRowClick = (pageId) => {
    navigate(`/pages/${pageId}`)
  }

  const handleCopyToClipboard = (pageUrl, pageId) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(pageUrl)
    } else {
      const textArea = document.createElement("textarea")
      textArea.value = pageUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
    }

    setTooltipOpen(pageId)
    setTimeout(() => {
      setTooltipOpen('')
    }, 1000)
  }

  const handleTooltipClose = () => {
    setTooltipOpen(false)
  }

  const renderSitemapItems = (sitemap) => (
    sitemap.map(page => {
      const isNew = newPages.includes(page.id)
      const isChecked = checkedPages.includes(page.id)

      return (
        <TreeItem
          key={page.id}
          className='hierarchicalPageRow'
          itemId={page.id}
          sx={{ backgroundColor: isNew || isChecked ? 'rgba(69, 116, 210, 0.1)' : 'inherit' }}
          label={
            <FormControlLabel
              className="hierarchicalPageItem"
              onClick={() => handleRowClick(page.id)}
              control={
                <Checkbox
                  sx={{ ml: 1 , p: 0 }}
                  checked={checkedPages.includes(page.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => handleToggle(event, page.id)}
                />
              }
              label={
                <Box className="hierarchicalPageItemContentContainer">
                  <Box className='pagesHierarchicalBodyCell pageUrl'>
                    <Typography>{page.url}</Typography>
                    <Tooltip
                      PopperProps={{
                        disablePortal: true,
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            backgroundColor: 'primary.main'
                          },
                        },
                      }}
                      onClose={handleTooltipClose}
                      open={tooltipOpen === page.id}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title="Copied to clipboard!"
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopyToClipboard(page.url, page.id)
                        }}
                        sx={{ height: "24px", position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', p: 0 }}
                      >
                        <ContentCopyIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box className='pagesHierarchicalBodyCell pageDate'>{page.updated_at ? formatDate(page.updated_at) : 'N/A'}</Box>
                  <Box className='pagesHierarchicalBodyCell pageDate'>{page.last_scanned_at ? formatDate(page.last_scanned_at) : 'Unscanned'}</Box>
                  <Box className='pagesHierarchicalBodyCell pageTests'>
                    {page.passed_ui_test_count || page.failed_ui_test_count ?
                      <Box className='pageTestsContentContainer'>
                        { page.passed_ui_test_count ?
                          <Box className='pagePassedTests'>
                            <Typography color="success.main">{page.passed_ui_test_count}</Typography>
                          </Box> : null
                        }
                        {page.failed_ui_test_count ?
                          <Box className='pageFailedTests'>
                            <Typography color="error.main">{page.failed_ui_test_count}</Typography>
                          </Box> : null
                        }
                      </Box> :
                      <Typography>-</Typography>
                    }
                  </Box>
                  <Box className="pagesHierarchicalBodyCell pageTestCases">
                    { page?.testCasesTotal ?
                      <Box className='pageTestCasesCountContainer'>
                        <Typography>{page.testCasesTotal}</Typography>
                      </Box> :
                      <Typography>-</Typography>
                    }
                  </Box>
                  <Box className="pagesHierarchicalBodyCell pageActions">
                    <IconButton className="pageActionsIconContainer" onClick={(e) => {e.stopPropagation(); handleOpenDeleteDialog(page?.id)}}>
                      <DeleteOutlineOutlinedIcon color='error' />
                    </IconButton>
                  </Box>
                </Box>
              }
            />
          }
        >
          {page.children ? renderSitemapItems(page.children) : null}
        </TreeItem>
      )
    })
  )

  return (
    <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={handleToggleExpansion}>
      {renderSitemapItems(sitemap)}
    </SimpleTreeView>
  )
}

export default PageList